import React, { useState, useEffect } from 'react';
import {
  Table, Tag, Button, Modal, Descriptions,
  Select, Input, Badge, Card, Row, Col, Statistic,
  message, Tooltip, Space, Spin
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined,
  EyeOutlined, ClockCircleOutlined, ReloadOutlined,
} from '@ant-design/icons';
import {
  collection, getDocs, doc, updateDoc,
  query, orderBy, serverTimestamp, addDoc, where
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { CARD_STYLE } from '../../constants/theme';

const { Option } = Select;
const { TextArea } = Input;

const KYC_TYPES = {
  dealer: { label: 'Dealer', color: '#1D9E75' },
  supplier: { label: 'Supplier', color: '#185FA5' },
  builder: { label: 'Builder', color: '#993556' },
  agent: { label: 'Agent', color: '#854F0B' },
  contractor: { label: 'Contractor', color: '#534AB7' },
  delivery_agent: { label: 'Delivery Agent', color: '#993C1D' },
};

const STATUS_CONFIG = {
  pending: { color: '#FAAD14', label: 'Pending', badge: 'warning' },
  approved: { color: '#52C41A', label: 'Approved', badge: 'success' },
  rejected: { color: '#FF4D4F', label: 'Rejected', badge: 'error' },
  more_info: { color: '#1890FF', label: 'More Info', badge: 'processing' },
};

export default function KYCQueue() {
  const [kycList, setKycList] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selectedKYC, setSelectedKYC] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [actionModal, setActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [reason, setReason] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadKYCData();
  }, []);

  useEffect(() => {
    let result = kycList;
    if (filterStatus !== 'all') result = result.filter(k => k.status === filterStatus);
    if (filterType !== 'all') result = result.filter(k => k.type === filterType);
    setFiltered(result);
  }, [filterStatus, filterType, kycList]);

  const loadKYCData = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'kycSubmissions'), orderBy('submittedAt', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        submittedAt: doc.data().submittedAt?.toDate?.()?.toLocaleDateString('en-IN') || 'N/A',
      }));
      setKycList(data);
      setFiltered(data);
    } catch (error) {
      console.error('Error loading KYC:', error);
      message.error('Failed to load KYC data');
    }
    setLoading(false);
  };

  const stats = {
    total: kycList.length,
    pending: kycList.filter(k => k.status === 'pending').length,
    approved: kycList.filter(k => k.status === 'approved').length,
    rejected: kycList.filter(k => k.status === 'rejected').length,
  };

  const handleAction = async () => {
    if (!reason && actionType === 'rejected') {
      message.warning('Please provide a reason for rejection');
      return;
    }
    setActionLoading(true);
    try {
      // Update in Firestore
      const kycRef = doc(db, 'kycSubmissions', selectedKYC.id);
      await updateDoc(kycRef, {
        status: actionType,
        rejectionReason: reason || '',
        reviewedAt: serverTimestamp(),
        reviewedBy: 'nksapcolouracademy@gmail.com',
      });

      // Write audit log
      await addDoc(collection(db, 'auditLogs'), {
        action: 'KYC ' + actionType.toUpperCase(),
        details: 'Applicant: ' + selectedKYC.applicantName + ' | Type: ' + selectedKYC.type + (reason ? ' | Reason: ' + reason : ''),
        adminEmail: 'nksapcolouracademy@gmail.com',
        targetId: selectedKYC.id,
        targetType: 'kyc',
        timestamp: serverTimestamp(),
      });

      // Update local state
      setKycList(prev => prev.map(k =>
        k.id === selectedKYC.id
          ? { ...k, status: actionType, rejectionReason: reason }
          : k
      ));

      message.success('KYC ' + actionType + ' successfully — saved to Firebase');
      setActionModal(false);
      setModalOpen(false);
      setReason('');
    } catch (error) {
      console.error('Action error:', error);
      message.error('Action failed. Check console.');
    }
    setActionLoading(false);
  };

  const columns = [
    {
      title: 'Applicant',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500, color: '#D4DCE3' }}>{record.applicantName}</div>
          <div style={{ fontSize: 11, color: '#7A8A96' }}>{record.businessName}</div>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      render: (type) => {
        const config = KYC_TYPES[type] || { label: type, color: '#7A8A96' };
        return (
          <Tag style={{
            background: config.color + '20',
            border: '0.5px solid ' + config.color + '50',
            color: config.color, borderRadius: 20, fontSize: 11,
          }}>
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: 'City',
      dataIndex: 'city',
      render: (city) => <span style={{ color: '#B0B8C1' }}>{city}</span>,
    },
    {
      title: 'Submitted',
      dataIndex: 'submittedAt',
      render: (date) => <span style={{ color: '#7A8A96', fontSize: 12 }}>{date}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
        return (
          <Badge
            status={config.badge}
            text={<span style={{ color: config.color, fontSize: 12 }}>{config.label}</span>}
          />
        );
      },
    },
    {
      title: 'Actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button type="text" icon={<EyeOutlined />} style={{ color: '#C9A84C' }}
              onClick={() => { setSelectedKYC(record); setModalOpen(true); }} />
          </Tooltip>
          {record.status === 'pending' && (
            <>
              <Tooltip title="Approve">
                <Button type="text" icon={<CheckCircleOutlined />} style={{ color: '#52C41A' }}
                  onClick={() => { setSelectedKYC(record); setActionType('approved'); setActionModal(true); }} />
              </Tooltip>
              <Tooltip title="Reject">
                <Button type="text" icon={<CloseCircleOutlined />} style={{ color: '#FF4D4F' }}
                  onClick={() => { setSelectedKYC(record); setActionType('rejected'); setActionModal(true); }} />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Stats Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          { title: 'Total KYC', value: stats.total, color: '#C9A84C' },
          { title: 'Pending Review', value: stats.pending, color: '#FAAD14' },
          { title: 'Approved', value: stats.approved, color: '#52C41A' },
          { title: 'Rejected', value: stats.rejected, color: '#FF4D4F' },
        ].map((stat, i) => (
          <Col xs={12} lg={6} key={i}>
            <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
              <Statistic
                title={<span style={{ color: '#7A8A96', fontSize: 12 }}>{stat.title}</span>}
                value={stat.value}
                valueStyle={{ color: stat.color, fontSize: 28, fontWeight: 600 }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filters */}
      <Card style={{ ...CARD_STYLE, marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ color: '#7A8A96', fontSize: 13 }}>Filter:</span>
          <Select value={filterStatus} onChange={setFilterStatus} style={{ width: 140 }}>
            <Option value="all">All Status</Option>
            <Option value="pending">Pending</Option>
            <Option value="approved">Approved</Option>
            <Option value="rejected">Rejected</Option>
            <Option value="more_info">More Info</Option>
          </Select>
          <Select value={filterType} onChange={setFilterType} style={{ width: 160 }}>
            <Option value="all">All Types</Option>
            <Option value="dealer">Dealer</Option>
            <Option value="supplier">Supplier</Option>
            <Option value="builder">Builder</Option>
            <Option value="agent">Agent</Option>
            <Option value="contractor">Contractor</Option>
            <Option value="delivery_agent">Delivery Agent</Option>
          </Select>
          <Button type="text" icon={<ReloadOutlined />} style={{ color: '#C9A84C' }}
            onClick={loadKYCData}>
            Refresh
          </Button>
          <span style={{ marginLeft: 'auto', color: '#7A8A96', fontSize: 12 }}>
            {loading ? 'Loading...' : `Showing ${filtered.length} of ${kycList.length} applications`}
          </span>
        </div>
      </Card>

      {/* KYC Table */}
      <Card style={CARD_STYLE} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            dataSource={filtered}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Spin>
      </Card>

      {/* View Details Modal */}
      <Modal
        title={<span style={{ color: '#C9A84C' }}>KYC Details - {selectedKYC?.applicantName}</span>}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        width={700}
        footer={
          selectedKYC?.status === 'pending' ? (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button onClick={() => setModalOpen(false)}>Close</Button>
              <Button danger icon={<CloseCircleOutlined />}
                onClick={() => { setActionType('rejected'); setActionModal(true); }}>
                Reject
              </Button>
              <Button icon={<CheckCircleOutlined />}
                style={{ background: 'linear-gradient(135deg, #E2C170, #9A7A2E)', border: 'none', color: '#0D0D0D', fontWeight: 600 }}
                onClick={() => { setActionType('approved'); setActionModal(true); }}>
                Approve
              </Button>
            </div>
          ) : <Button onClick={() => setModalOpen(false)}>Close</Button>
        }
      >
        {selectedKYC && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Full Name">{selectedKYC.applicantName}</Descriptions.Item>
            <Descriptions.Item label="Business">{selectedKYC.businessName}</Descriptions.Item>
            <Descriptions.Item label="Type">
              <Tag color={KYC_TYPES[selectedKYC.type]?.color}>
                {KYC_TYPES[selectedKYC.type]?.label}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Badge status={STATUS_CONFIG[selectedKYC.status]?.badge}
                text={STATUS_CONFIG[selectedKYC.status]?.label} />
            </Descriptions.Item>
            <Descriptions.Item label="Phone">{selectedKYC.phone}</Descriptions.Item>
            <Descriptions.Item label="Email">{selectedKYC.email}</Descriptions.Item>
            <Descriptions.Item label="City">{selectedKYC.city}</Descriptions.Item>
            <Descriptions.Item label="District">{selectedKYC.district}</Descriptions.Item>
            <Descriptions.Item label="Pincode">{selectedKYC.pincode}</Descriptions.Item>
            <Descriptions.Item label="Submitted">{selectedKYC.submittedAt}</Descriptions.Item>
            <Descriptions.Item label="GST Number">{selectedKYC.gst}</Descriptions.Item>
            <Descriptions.Item label="Aadhaar">{selectedKYC.aadhaar}</Descriptions.Item>
            <Descriptions.Item label="PAN">{selectedKYC.pan}</Descriptions.Item>
            <Descriptions.Item label="Bank">{selectedKYC.bankName} — {selectedKYC.ifsc}</Descriptions.Item>
            {selectedKYC.rera && (
              <Descriptions.Item label="RERA Number" span={2}>{selectedKYC.rera}</Descriptions.Item>
            )}
            <Descriptions.Item label="Documents" span={2}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {selectedKYC.documentNames?.map((d, i) => (
                  <Tag key={i} style={{
                    background: 'rgba(201,168,76,0.1)',
                    border: '0.5px solid rgba(201,168,76,0.3)',
                    color: '#C9A84C',
                  }}>
                    {d}
                  </Tag>
                ))}
              </div>
            </Descriptions.Item>
            {selectedKYC.adminNotes && (
              <Descriptions.Item label="Admin Notes" span={2}>
                <span style={{ color: '#FAAD14' }}>{selectedKYC.adminNotes}</span>
              </Descriptions.Item>
            )}
            {selectedKYC.rejectionReason && (
              <Descriptions.Item label="Rejection Reason" span={2}>
                <span style={{ color: '#FF4D4F' }}>{selectedKYC.rejectionReason}</span>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Source" span={2}>
              <Tag style={{ background: 'rgba(24,144,255,0.1)', color: '#1890FF', border: 'none', borderRadius: 20 }}>
                {selectedKYC.appSource}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Action Modal */}
      <Modal
        title={
          <span style={{ color: actionType === 'approved' ? '#52C41A' : '#FF4D4F' }}>
            {actionType === 'approved' ? 'Approve KYC' : 'Reject KYC'} - {selectedKYC?.applicantName}
          </span>
        }
        open={actionModal}
        onCancel={() => { setActionModal(false); setReason(''); }}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => { setActionModal(false); setReason(''); }}>Cancel</Button>
            <Button loading={actionLoading} onClick={handleAction}
              style={{
                background: actionType === 'approved'
                  ? 'linear-gradient(135deg, #E2C170, #9A7A2E)' : '#FF4D4F',
                border: 'none', color: '#fff', fontWeight: 600,
              }}>
              Confirm {actionType === 'approved' ? 'Approval' : 'Rejection'}
            </Button>
          </div>
        }
      >
        <div style={{ marginBottom: 16, color: '#B0B8C1' }}>
          {actionType === 'approved'
            ? 'This will activate the account. Change will be saved to Firebase instantly.'
            : 'Please provide a reason. This will be sent to the applicant.'}
        </div>
        <TextArea rows={3}
          placeholder={actionType === 'approved' ? 'Optional notes...' : 'Required: Reason for rejection...'}
          value={reason} onChange={e => setReason(e.target.value)}
          style={{ background: '#222222', border: '0.5px solid rgba(201,168,76,0.3)', color: '#D4DCE3' }}
        />
      </Modal>
    </div>
  );
}